import { ALERT_TYPES, SENSITIVITY_LEVELS } from "@observe-tech/utils";
import {
  fail,
  oneOf,
  bool,
  nonEmptyString,
  ISO_8601_PREFIX,
} from "../infra/validation";
import type { Notification, NotificationSettings } from "../types";

export const NOTIFICATION_SUBTYPES: Record<string, readonly string[] | null> = {
  benchmark: ["pellets_threshold_exceeded"],
  equipment: [
    "unresponsive_winch_system",
    "winch_manual_control",
    "unresponsive_feed_system",
  ],
  goal: [
    "feeding_response",
    "limit_grazing",
    "feed_profile_smoothness",
    "camera_movement",
  ],
  automation: ["automation_event_triggered", "automation_on_demand_failure"],
  environment: null,
};

export const assertValidNotification = (record: Notification): void => {
  if (typeof record.id !== "number") fail("notification.id must be a number");
  if (typeof record.machineId !== "number")
    fail("notification.machineId must be a number");
  if (typeof record.siteId !== "number")
    fail("notification.siteId must be a number");
  if (record.alertType != null)
    oneOf(record.alertType, ALERT_TYPES, "notification.alertType");
  if (record.critical != null) bool(record.critical, "notification.critical");
  if (
    record.metadata != null &&
    (typeof record.metadata !== "object" || Array.isArray(record.metadata))
  )
    fail("notification.metadata must be an object or null");
  if (record.type === "benchmark") {
    const meta = (record.metadata ?? {}) as Record<string, unknown>;
    if (typeof meta.nPellets !== "number")
      fail("benchmark metadata needs numeric nPellets");
  }
};

export const notificationVocabularyIssue = (
  record: Notification,
): string | null => {
  const allowed = NOTIFICATION_SUBTYPES[record.type];
  if (allowed === undefined) return `unknown notification type: ${record.type}`;
  if (allowed !== null && !allowed.includes(record.subtype))
    return `subtype ${record.subtype} is not valid for type ${record.type}`;
  return null;
};

export const assertValidNotificationSettings = (
  settings: NotificationSettings,
): void => {
  bool(settings.muteForToday, "notificationSettings.muteForToday");
  if (
    settings.muteTimestamp != null &&
    !ISO_8601_PREFIX.test(settings.muteTimestamp)
  )
    fail(
      `notificationSettings.muteTimestamp is not iso-8601: ${settings.muteTimestamp}`,
    );
  for (const [key, value] of Object.entries(settings)) {
    if (key === "muteForToday" || key === "muteTimestamp") continue;
    if (key.endsWith(".enabled") || key.endsWith(".isCritical"))
      bool(value, key);
    else if (key.endsWith(".alertType")) oneOf(value, ALERT_TYPES, key);
    else if (key.endsWith(".alertSound")) nonEmptyString(value, key);
    else if (key.endsWith(".sensitivity")) {
      if (typeof value === "number") {
        if (value < 1) fail(`${key} must be >= 1`);
      } else oneOf(value, SENSITIVITY_LEVELS, key);
    }
  }
};
